/**
 * FikoMakro3DViewer — Makro 3D gösterimi için TAM motor (FikoThreeJsComponent) gömülü viewer.
 * Diagonal/Ozmo kafa ile AYNI gömme tekniği (FikoMini3DDialogBase) — ama kafa/gimbal/takım YOK,
 * sadece temiz FikoSahne üzerine makro hareket yolu + düğümler çizilir. Çift-tık → adım seç (düzenle).
 */
import { Component, ChangeDetectionStrategy, ChangeDetectorRef, Injector, ViewContainerRef, EnvironmentInjector, NgZone, ViewChild, ElementRef, Input, Output, EventEmitter, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as THREE from 'three';
import { FikoCamMini3DBase } from './fiko-cam-mini-3d.base';

export interface IMakroYolNokta { x: number; y: number; z: number; ai?: number; }

@Component({
  selector: 'fiko-makro-3d',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `<div #viewerContainer class="mk3d-vc" [style.height.px]="viewerYukseklik"></div>`,
  styles: [`
    .mk3d-vc{position:relative;width:100%;overflow:hidden;border-radius:6px;border:1px solid rgba(0,200,255,.2);background:#0a1622;}
    .mk3d-vc ::ng-deep canvas{display:block;width:100%!important;height:100%!important;}
  `]
})
export class FikoMakro3DViewer extends FikoCamMini3DBase implements OnChanges {
  @ViewChild('viewerContainer', { static: true }) viewerContainerRef!: ElementRef<HTMLDivElement>;

  @Input() yol: IMakroYolNokta[] = [];   // LOCAL hareket noktaları (engagement = origin)
  @Input() etiketler: { x: number; y: number; z: number; metin: string; ai: number }[] = [];   // 3D ölçü etiketleri
  @Input() renk = 0x34d399;
  @Input() seciliAi: number | null = null;
  @Output() adimSec = new EventEmitter<number>();
  @Output() olcuDegis = new EventEmitter<{ ai: number; d: number }>();   // düğüm sürükle → ölçü artışı (dünya birimi)

  private grup: THREE.Group | null = null;
  private _ext = 8;
  private dblH: ((e: MouseEvent) => void) | null = null;
  private downH: ((e: PointerEvent) => void) | null = null;
  private moveH: ((e: PointerEvent) => void) | null = null;
  private upH: ((e: PointerEvent) => void) | null = null;
  private _drag: { ai: number; axX: number; axY: number; ppu: number } | null = null;
  private _lastXY = { x: 0, y: 0 };
  private _moved = false;
  private _rc = new THREE.Raycaster();
  private _v2 = new THREE.Vector2();

  constructor(cd: ChangeDetectorRef, injector: Injector, viewContainerRef: ViewContainerRef, environmentInjector: EnvironmentInjector, ngZone: NgZone) {
    super(cd, injector, viewContainerRef, environmentInjector, ngZone);
    this.viewerGenislik = 232; this.viewerYukseklik = 210;
  }

  override sahneHazir(): void {
    if (!this.sahne) return;
    const cv: HTMLCanvasElement | undefined = (this.sahne as any)?.anaGosterim?.canvas?.nativeElement;
    if (cv && !this.downH) {
      this.dblH = (e) => this.ciftTik(e); cv.addEventListener('dblclick', this.dblH);
      this.downH = (e) => this.dragBasla(e); cv.addEventListener('pointerdown', this.downH, true);   // düğüm sürükle = ölçü değiştir
    }
    this.ciz();
    setTimeout(() => this.cercevele(), 80);
  }

  ngOnChanges(): void { if (this.hazirMi) { this.ciz(); } }

  /** Makro hareket yolunu çiz (tube + düğümler + ok). */
  private ciz(): void {
    const s: any = this.sahne; if (!s) return;
    if (this.grup) { s.remove(this.grup); this.grup.traverse((o: any) => { o.geometry?.dispose?.(); if (o.material) (Array.isArray(o.material) ? o.material : [o.material]).forEach((m: any) => { m.map?.dispose?.(); m.dispose?.(); }); }); }
    const g = new THREE.Group(); g.name = 'MakroYol'; this.grup = g;
    const renk = this.renk;
    const pts = this.yol || [];
    // hareketin kapsamına göre referans boyutu (sabit 44 yerine — küçük hareket kaybolmasın)
    let ext = 8; for (const p of pts) ext = Math.max(ext, Math.abs(p.x), Math.abs(p.y), Math.abs(p.z));
    this._ext = ext; const blkS = ext * 2.2;
    // referans malzeme zemini (yarı saydam) + grid
    const blk = new THREE.Mesh(new THREE.BoxGeometry(blkS, blkS, ext * 0.5), new THREE.MeshStandardMaterial({ color: 0x5f7d9c, transparent: true, opacity: 0.1, metalness: .2, roughness: .85 }));
    blk.position.set(0, 0, -ext * 0.25); g.add(blk);
    const grid = new THREE.GridHelper(blkS, 10, 0x88aacc, 0x2c4456); grid.rotation.x = Math.PI / 2; g.add(grid);
    if (pts.length >= 2) {
      const nodeR = Math.max(0.45, ext * 0.05);   // KÜÇÜK düzenleme noktaları (rahat kullanım)
      const vec = pts.map(p => new THREE.Vector3(p.x, p.y, p.z));
      // NORMAL çizgi (boru değil) — düz hareket yolu
      const lgeo = new THREE.BufferGeometry().setFromPoints(vec);
      const line = new THREE.Line(lgeo, new THREE.LineBasicMaterial({ color: renk, transparent: true, opacity: 0.95 }));
      g.add(line);
      for (const p of pts) {
        if (p.ai == null) continue;
        const sec = this.seciliAi === p.ai;
        const sp = new THREE.Mesh(new THREE.SphereGeometry(sec ? nodeR * 1.5 : nodeR, 16, 14), new THREE.MeshStandardMaterial({ color: sec ? 0xffd24d : 0xffffff, emissive: sec ? 0xffd24d : renk, emissiveIntensity: .55 }));
        sp.position.set(p.x, p.y, p.z); sp.userData['ai'] = p.ai; sp.userData['kind'] = 'dugum'; sp.name = 'MakroDugum'; g.add(sp);
      }
      const a = pts[pts.length - 2], b = pts[pts.length - 1];
      const dir = new THREE.Vector3(b.x - a.x, b.y - a.y, b.z - a.z);
      if (dir.lengthSq() > 1e-6) {
        const ok = new THREE.Mesh(new THREE.ConeGeometry(nodeR * 1.5, nodeR * 4, 14), new THREE.MeshStandardMaterial({ color: renk, emissive: renk, emissiveIntensity: .4 }));
        ok.position.set(b.x, b.y, b.z); ok.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.normalize()); g.add(ok);
      }
      // 3D ÖLÇÜ ETİKETLERİ (neyin ne ölçüsü) — çift-tık → düzelt. Üst üste binmesin diye index'e göre dikey kademe.
      const etk = this.etiketler || [];
      etk.forEach((et, i) => {
        const sprite = this.etiketSprite(et.metin, et.ai === this.seciliAi ? 0xffd24d : renk, ext);
        sprite.position.set(et.x + ext * 0.25, et.y, et.z + ext * (0.35 + i * 0.4)); sprite.userData['ai'] = et.ai; sprite.userData['kind'] = 'etiket'; g.add(sprite);
        // etiket → düğüm bağlantı çizgisi (hangi ölçü hangi nokta belli olsun)
        const lg = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(et.x, et.y, et.z), sprite.position.clone()]);
        const ll = new THREE.Line(lg, new THREE.LineBasicMaterial({ color: renk, transparent: true, opacity: 0.4 })); g.add(ll);
      });
    }
    s.add(g);
    if (typeof s.anaGosterim?.ciz === 'function') s.anaGosterim.ciz();
  }

  /** Ölçü etiketi sprite'ı (canvas yazı). */
  private etiketSprite(metin: string, renk: number, ext: number): THREE.Sprite {
    const W = 256, H = 72, cv = document.createElement('canvas'); cv.width = W; cv.height = H; const ctx = cv.getContext('2d')!;
    const hex = '#' + renk.toString(16).padStart(6, '0');
    ctx.fillStyle = 'rgba(8,18,30,.92)'; this.yuvarlakDik(ctx, 4, 4, W - 8, H - 8, 14); ctx.fill();
    ctx.strokeStyle = hex; ctx.lineWidth = 3; this.yuvarlakDik(ctx, 4, 4, W - 8, H - 8, 14); ctx.stroke();
    ctx.fillStyle = '#eaf6ff'; ctx.font = 'bold 38px system-ui,sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillText(metin, W / 2, H / 2 + 2);
    const tex = new THREE.CanvasTexture(cv); tex.colorSpace = THREE.SRGBColorSpace;
    const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, depthTest: false, transparent: true }));
    const sc = Math.max(1.8, ext * 0.22); sp.scale.set(sc * (W / H), sc, 1); sp.renderOrder = 999; return sp;
  }
  private yuvarlakDik(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
    ctx.beginPath(); ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r); ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
  }

  /** İZOMETRİK kamera + harekete göre çerçevele. */
  private cercevele(): void {
    const ag: any = (this.sahne as any)?.anaGosterim; const kam = ag?.aktifKamera; if (!kam || !this.grup) return;
    const d = Math.max(12, this._ext * 3);
    kam.position.set(d, -d, d); if (kam.lookAt) kam.lookAt(0, 0, this._ext * 0.1);   // izometrik (eşit açı); etiketler yukarıda → hedef hafif yukarı
    if (kam.isOrthographicCamera) { kam.zoom = Math.max(1.5, 38 / Math.max(1, this._ext)); kam.updateProjectionMatrix?.(); }
    if (ag.aktifKontrol?.target) { ag.aktifKontrol.target.set(0, 0, this._ext * 0.1); ag.aktifKontrol.update?.(); }
    ag.ciz?.();
  }

  /** İmleç altındaki makro düğümünü/etiketini bul. */
  private dugumBul(e: MouseEvent): { ai: number; kind: string } | null {
    const ag: any = (this.sahne as any)?.anaGosterim; const kam = ag?.aktifKamera; const cv = ag?.canvas?.nativeElement; if (!kam || !cv || !this.grup) return null;
    const r = cv.getBoundingClientRect();
    this._v2.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
    this._rc.setFromCamera(this._v2, kam);
    const hit = this._rc.intersectObjects(this.grup.children, true).find(i => (i.object as any).userData?.['ai'] != null);
    return hit ? { ai: (hit.object as any).userData['ai'], kind: (hit.object as any).userData['kind'] } : null;
  }
  private projeksiyon(v: THREE.Vector3): { x: number; y: number } {
    const ag: any = (this.sahne as any)?.anaGosterim; const kam = ag.aktifKamera; const cv = ag.canvas.nativeElement; const r = cv.getBoundingClientRect();
    const p = v.clone().project(kam); return { x: (p.x * 0.5 + 0.5) * r.width, y: (-p.y * 0.5 + 0.5) * r.height };
  }
  private ciftTik(e: MouseEvent): void { const d = this.dugumBul(e); if (d) { e.stopPropagation(); this.ngZone.run(() => this.adimSec.emit(d.ai)); } }

  /** Düğüme bas → sürükle = ölçü değiştir (segment yönü boyunca). Boşluğa basınca orbit serbest. */
  private dragBasla(e: PointerEvent): void {
    if (e.button !== 0) return;   // SADECE SOL tuş; orta/sağ → kamera serbest
    const d = this.dugumBul(e); if (!d || d.kind !== 'dugum') return;   // sadece düğüm sürüklenir (etiket/boşluk orbit'e bırakılır)
    const idx = this.yol.findIndex(p => p.ai === d.ai); if (idx < 0) return;
    const cur = this.yol[idx], prev = this.yol[idx - 1] || { x: 0, y: 0, z: 0 };
    const dir = new THREE.Vector3(cur.x - prev.x, cur.y - prev.y, cur.z - prev.z); if (dir.lengthSq() < 1e-6) dir.set(0, 0, -1); dir.normalize();
    const nodeW = new THREE.Vector3(cur.x, cur.y, cur.z);
    const s0 = this.projeksiyon(nodeW), s1 = this.projeksiyon(nodeW.clone().add(dir));
    let ax = { x: s1.x - s0.x, y: s1.y - s0.y }; const ppu = Math.hypot(ax.x, ax.y) || 1;
    this._drag = { ai: d.ai, axX: ax.x / ppu, axY: ax.y / ppu, ppu };
    this._lastXY = { x: e.clientX, y: e.clientY }; this._moved = false;
    const ag: any = (this.sahne as any)?.anaGosterim; if (ag?.aktifKontrol) ag.aktifKontrol.enabled = false;   // orbit'i durdur
    e.stopPropagation(); e.preventDefault();
    this.moveH = (ev) => this.dragHareket(ev); this.upH = () => this.dragBitir();
    window.addEventListener('pointermove', this.moveH, true); window.addEventListener('pointerup', this.upH, true);
  }
  private dragHareket(e: PointerEvent): void {
    const s = this._drag; if (!s) return;
    const dx = e.clientX - this._lastXY.x, dy = e.clientY - this._lastXY.y; this._lastXY = { x: e.clientX, y: e.clientY };
    const dPix = dx * s.axX + dy * s.axY;   // segment-ekseni boyunca ekran kayması
    const dWorld = dPix / s.ppu;
    if (Math.abs(dWorld) > 1e-4) { this._moved = true; this.ngZone.run(() => this.olcuDegis.emit({ ai: s.ai, d: dWorld })); }
  }
  private dragBitir(): void {
    const s = this._drag;
    if (this.moveH) window.removeEventListener('pointermove', this.moveH, true);
    if (this.upH) window.removeEventListener('pointerup', this.upH, true);
    this.moveH = this.upH = null;
    const ag: any = (this.sahne as any)?.anaGosterim; if (ag?.aktifKontrol) ag.aktifKontrol.enabled = true;
    if (s && !this._moved) this.ngZone.run(() => this.adimSec.emit(s.ai));   // sürüklemeden bıraktı → seç
    this._drag = null;
  }

  protected override temizle(): void {
    const cv: HTMLCanvasElement | undefined = (this.sahne as any)?.anaGosterim?.canvas?.nativeElement;
    if (cv && this.dblH) cv.removeEventListener('dblclick', this.dblH);
    if (cv && this.downH) cv.removeEventListener('pointerdown', this.downH, true);
    if (this.moveH) window.removeEventListener('pointermove', this.moveH, true);
    if (this.upH) window.removeEventListener('pointerup', this.upH, true);
    this.dblH = this.downH = this.moveH = this.upH = null;
  }
}
